import { Component } from '@theme/component';
import { renderPrice } from '@theme/configurator-shared';

/**
 * @typedef {object} LakenSize
 * @property {number} id
 * @property {string} size
 * @property {number} priceCents
 * @property {boolean} available
 * @property {string} featuredImage
 */

/**
 * @typedef {object} LakenCard
 * @property {string} farbe
 * @property {string} bettwaescheTitle
 * @property {string} lakenFarbe
 * @property {number} lakenDefaultVariantId
 * @property {number} lakenDefaultPriceCents
 * @property {number | null} lakenDefaultCompareAtPriceCents
 * @property {string} lakenFeaturedImage
 * @property {string} lifestyleImage
 * @property {LakenSize[]} lakenSizes
 */

/**
 * @typedef {object} ConfiguratorVariant
 * @property {number} id
 * @property {string} farbe
 * @property {string} kissenGroesse
 * @property {string} deckeGroesse
 * @property {string} lieferzeit
 * @property {number} priceCents
 * @property {number | null} compareAtPriceCents
 * @property {boolean} available
 */

/**
 * @typedef {object} LakenRecommendationRefs
 * @property {HTMLScriptElement} cardData
 */

/**
 * @extends Component<LakenRecommendationRefs>
 */
class LakenRecommendation extends Component {
  requiredRefs = ['cardData'];

  /** @type {LakenCard[]} */
  #cards = [];

  /**
   * Last-seen Farbe from a `configurator:state` event, used to detect an
   * actual color change (as opposed to a Kissen/Decke-only price update that
   * re-fires the same event type) so the matching card is only reordered to
   * the front when the shopper's Bettwäsche color selection actually changes.
   * @type {string | null}
   */
  #lastFarbe = null;

  /**
   * Debounces the cross-color reprice fetch wave — `configurator:state`
   * fires on every Kissen/Decke/Farbe pill click, and firing N parallel
   * fetches (one per Laken card color) on every intermediate click during
   * rapid interaction would be wasteful. Only the shopper's SETTLED
   * selection (no further event for this long) triggers the fetch wave.
   * See docs/superpowers/specs/2026-08-27-laken-card-cross-color-pricing-design.md.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #repriceDebounceTimer = null;

  /**
   * Aborts a still-in-flight cross-color fetch wave if a NEWER
   * `configurator:state` event (after its own debounce delay) supersedes it
   * — without this, a slow wave triggered by an earlier selection could
   * resolve and overwrite prices AFTER a faster wave from a later selection
   * already applied the correct ones.
   * @type {AbortController}
   */
  #repriceAbortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#cards = JSON.parse(this.refs.cardData.textContent ?? '[]');
    document.addEventListener('configurator:state', this.#handleConfiguratorState);

    // Custom-element connection/upgrade order across separate elements on the
    // page is not something this component controls or can rely on. Today,
    // `<configurator-variant-picker>` lives in an earlier section ("main") and
    // dispatches its OWN first `configurator:state` event SYNCHRONOUSLY at the
    // end of ITS `connectedCallback` (via `#render()` → `#dispatchStateEvent()`)
    // — meaning it fires and is lost BEFORE this element has even connected,
    // let alone registered the listener above. Without this call, every card
    // would silently show its server-rendered, Laken-only initial price until
    // the shopper happened to interact with the configurator afterward.
    //
    // This eagerly pulls whatever state is available RIGHT NOW (a no-op if the
    // configurator hasn't connected/rendered yet — see `#syncFromConfigurator`)
    // to cover the "configurator connects first" ordering, while the listener
    // registered above remains in place to catch the configurator's first (or
    // only) event normally if it instead connects AFTER this element — user-
    // driven changes always fire after both elements have connected, so the
    // listener alone is always sufficient for those regardless of ordering.
    this.#syncFromConfigurator();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('configurator:state', this.#handleConfiguratorState);
    if (this.#repriceDebounceTimer !== null) clearTimeout(this.#repriceDebounceTimer);
    this.#repriceAbortController.abort();
  }

  /**
   * Handles the configurator's `configurator:state` event (dispatched by the
   * separate, already-shipped `<configurator-variant-picker>` element
   * elsewhere on the page). Every event reprices all cards to
   * "configurator total + this card's own Laken price"; only an actual
   * Farbe change (not every event) reorders the matching card to the front.
   * @param {Event} event
   */
  #handleConfiguratorState = (event) => {
    const { farbe, priceCents, compareAtCents } = /** @type {{ farbe: string, priceCents: number, compareAtCents: number | null }} */ (
      /** @type {unknown} */ (event)
    );
    this.#applyConfiguratorState(farbe, priceCents, compareAtCents);
  };

  /**
   * Shared by the `configurator:state` event handler and the one-time
   * connect-time pull (`#syncFromConfigurator`), so both paths reprice/reorder
   * identically and can never silently diverge.
   *
   * Reprices the MATCHING card immediately, synchronously, using the totals
   * already known from this event — no fetch needed, since that's exactly
   * what the live configurator itself is currently showing. Every OTHER
   * card's own price is stale until the debounced cross-color fetch wave
   * (see `#scheduleRepriceOtherCards`) resolves — this keeps the matching
   * card always instantly correct while the rest catch up shortly after.
   * @param {string} farbe
   * @param {number} priceCents
   * @param {number | null} compareAtCents
   */
  #applyConfiguratorState(farbe, priceCents, compareAtCents) {
    this.#repriceMatchingCard(farbe, priceCents, compareAtCents);
    this.#scheduleRepriceOtherCards(farbe);

    if (farbe !== this.#lastFarbe) {
      this.#lastFarbe = farbe;
      this.#reorderCards(farbe);
    }
  }

  /**
   * One-time, synchronous pull of the configurator's CURRENT state, read
   * directly from its already-rendered DOM/embedded JSON — the same
   * cross-component DOM-read approach `#readConfiguratorVariants` /
   * `#readConfiguratorKissenSelection` / `#readConfiguratorDecke` already use
   * elsewhere in this file, applied to one more concern (current Farbe) so
   * this fix doesn't require any new public API on
   * `<configurator-variant-picker>`.
   *
   * No-ops silently (leaving the server-rendered initial prices as-is) if the
   * configurator element isn't found yet, or hasn't rendered its Farbe pills
   * yet — both mean it hasn't connected/rendered at all, in which case its own
   * `connectedCallback` will dispatch a `configurator:state` event that the
   * listener registered in `connectedCallback` (above) will catch normally.
   */
  #syncFromConfigurator() {
    const farbe = this.#readConfiguratorFarbe();
    if (!farbe) return;

    const configuratorVariants = this.#readConfiguratorVariants();
    const kissen = this.#readConfiguratorKissenSelection();
    const decke = this.#readConfiguratorDecke();
    const { priceCents, compareAtCents } = this.#resolveConfiguratorPriceCents(configuratorVariants, kissen, decke, farbe);

    this.#applyConfiguratorState(farbe, priceCents, compareAtCents > priceCents ? compareAtCents : null);
  }

  /**
   * Reads which Farbe is CURRENTLY SELECTED in the live configurator, the
   * same DOM-read way `#readConfiguratorKissenSelection` /
   * `#readConfiguratorDecke` read their own state.
   *
   * Per `blocks/configurator-variant-picker.liquid`, the `ref="farbeGroup"`
   * container (rendered class `configurator-variant-picker__farbe`) holds one
   * `<input type="radio" name="configurator-farbe-{{ block.id }}">` pill per
   * Farbe option, with the currently-selected one carrying `checked`.
   * @returns {string | null}
   */
  #readConfiguratorFarbe() {
    const configuratorEl = document.querySelector('configurator-variant-picker');
    const farbeGroup = configuratorEl?.querySelector('.configurator-variant-picker__farbe');
    if (!farbeGroup) return null;

    const checkedInput = farbeGroup.querySelector('input[type="radio"]:checked');
    return checkedInput instanceof HTMLInputElement ? checkedInput.value : null;
  }

  /**
   * Index of the first Kissen instance whose size has any Decke-paired
   * variant among `configuratorVariants`, for the given `farbe` — or -1 if
   * none do. Local port of `assets/configurator-variant-picker.js`'s own
   * `#deckePairingIndex()`: that component pairs the Decke with whichever
   * Kissen instance is actually pairable, not always instance 0 (e.g. the
   * 40/40 neck-pillow size has no Decke pairing, so a later Kissen instance
   * — added via "+ Bettwäsche Set hinzufügen" — carries the Decke instead).
   * `#resolveConfiguratorPriceCents` below has to re-derive this itself
   * since it reads the configurator's state from its rendered DOM, not by
   * calling the configurator's own methods directly. See
   * docs/superpowers/specs/2026-09-03-bettwaesche-set-for-unpairable-kissen-design.md.
   * @param {ConfiguratorVariant[]} configuratorVariants
   * @param {{ kissenGroesse: string }[]} kissen
   * @param {string} farbe
   * @returns {number}
   */
  #configuratorDeckePairingIndex(configuratorVariants, kissen, farbe) {
    return kissen.findIndex((instance) =>
      configuratorVariants.some((v) => v.farbe === farbe && v.kissenGroesse === instance.kissenGroesse && v.deckeGroesse !== '')
    );
  }

  /**
   * Resolves the configurator's current Kissen/Decke selection to a total
   * price and a parallel "was" total, mirroring
   * `assets/configurator-variant-picker.js`'s own
   * `#resolvedVariants`/`#priceTotals`: whichever Kissen instance
   * `#configuratorDeckePairingIndex` resolves against the current Decke
   * selection (bare variant for "Keine Decke", combined variant
   * otherwise), every other instance always resolves against its bare
   * variant. Unmatched instances contribute 0 to both totals, same as
   * the configurator's own `#resolvedVariants` silently skipping
   * non-matches. Each matched variant contributes its own
   * compareAtPriceCents to the "was" total only when it's actually a
   * discount, otherwise its regular price — same "neutral contribution"
   * rule as `#priceTotals`. See
   * docs/superpowers/specs/2026-08-27-configurator-sale-price-design.md.
   * @param {ConfiguratorVariant[]} configuratorVariants
   * @param {{ kissenGroesse: string }[]} kissen
   * @param {{ deckeGroesse: string } | null} decke
   * @param {string} farbe
   * @returns {{ priceCents: number, compareAtCents: number }}
   */
  #resolveConfiguratorPriceCents(configuratorVariants, kissen, decke, farbe) {
    /** @type {ConfiguratorVariant[]} */
    const matches = [];
    const pairingIndex = this.#configuratorDeckePairingIndex(configuratorVariants, kissen, farbe);
    kissen.forEach((instance, index) => {
      const deckeGroesse = index === pairingIndex ? decke?.deckeGroesse ?? '' : '';
      const match = configuratorVariants.find(
        (v) => v.farbe === farbe && v.kissenGroesse === instance.kissenGroesse && v.deckeGroesse === deckeGroesse
      );
      if (match) matches.push(match);
    });

    const priceCents = matches.reduce((sum, v) => sum + v.priceCents, 0);
    const compareAtCents = matches.reduce((sum, v) => {
      const contribution = v.compareAtPriceCents && v.compareAtPriceCents > v.priceCents ? v.compareAtPriceCents : v.priceCents;
      return sum + contribution;
    }, 0);
    return { priceCents, compareAtCents };
  }

  /**
   * Reprices ONLY the card matching the currently-selected Bettwäsche color,
   * using the totals already known from the live `configurator:state` event
   * — no fetch needed, this IS the live configurator's own current total.
   * @param {string} currentFarbe
   * @param {number} configuratorPriceCents
   * @param {number | null} configuratorCompareAtCents
   */
  #repriceMatchingCard(currentFarbe, configuratorPriceCents, configuratorCompareAtCents) {
    const card = this.#cards.find((c) => c.farbe === currentFarbe);
    if (!card) return;

    this.#renderCardPrice(card, configuratorPriceCents, configuratorCompareAtCents ?? configuratorPriceCents);
  }

  /**
   * Renders one card's combined price: `configuratorPriceCents` (that
   * card's OWN color's Bettwäsche price for the current Kissen/Decke
   * selection) plus that card's own Laken default price, with a parallel
   * "was" total folding in whichever side(s) are actually discounted.
   * Each side contributes its own compare-at value to the "was" total only
   * when it's a real discount, otherwise its regular price — same "neutral
   * contribution" rule used everywhere else this session. Both discounts
   * stack when both sides genuinely have their own — e.g. a card whose own
   * Bettwäsche color AND own Laken are both on sale legitimately shows the
   * sum of both savings. See
   * docs/superpowers/specs/2026-08-27-laken-card-cross-color-pricing-design.md.
   * @param {LakenCard} card
   * @param {number} bettwaeschePriceCents
   * @param {number} bettwaescheCompareAtCents
   */
  #renderCardPrice(card, bettwaeschePriceCents, bettwaescheCompareAtCents) {
    const moneyFormat = this.dataset.moneyFormat ?? '';
    const currency = this.dataset.currency ?? 'USD';

    const cardEl = this.querySelector(`.laken-recommendation-card[data-farbe="${CSS.escape(card.farbe)}"]`);
    const priceEl = cardEl?.querySelector('.laken-recommendation-card__price');
    if (!priceEl) return;

    const totalCents = bettwaeschePriceCents + card.lakenDefaultPriceCents;
    const lakenCompareAtContribution =
      card.lakenDefaultCompareAtPriceCents && card.lakenDefaultCompareAtPriceCents > card.lakenDefaultPriceCents
        ? card.lakenDefaultCompareAtPriceCents
        : card.lakenDefaultPriceCents;
    const compareAtTotalCents = bettwaescheCompareAtCents + lakenCompareAtContribution;

    renderPrice(priceEl, totalCents, compareAtTotalCents, moneyFormat, currency);
  }

  /**
   * Debounces a fetch-and-reprice wave for every card OTHER than the one
   * matching `currentFarbe` (that one's already handled synchronously by
   * `#repriceMatchingCard`). `configurator:state` fires on every
   * Kissen/Decke/Farbe pill click — only the shopper's settled selection
   * (no further event for ~400ms) triggers the actual fetch wave, to avoid
   * firing N parallel requests (N = distinct card colors) on every
   * intermediate click during rapid interaction. See
   * docs/superpowers/specs/2026-08-27-laken-card-cross-color-pricing-design.md.
   * @param {string} currentFarbe
   */
  #scheduleRepriceOtherCards(currentFarbe) {
    if (this.#repriceDebounceTimer !== null) clearTimeout(this.#repriceDebounceTimer);

    this.#repriceDebounceTimer = setTimeout(() => {
      this.#repriceDebounceTimer = null;
      this.#repriceOtherCards(currentFarbe);
    }, 400);
  }

  /**
   * Fetches every OTHER card's own color-scoped variant set in parallel
   * (reusing `#fetchVariantsForFarbe`, the same mechanism `handleCtaClick`
   * already uses for a different purpose) and reprices each card using its
   * own resolved total — not the currently-selected color's total. Cards
   * whose fetch fails (network error, or the color has no matching
   * variants for the current Kissen/Decke selection) are left showing
   * whatever price they last had; a card is never regressed to a blank or
   * error state by a failed background reprice.
   *
   * A newer call to this method (from a later `configurator:state` event,
   * after its own debounce delay) aborts this wave's still-in-flight
   * fetches via `#repriceAbortController` — without this, a slow wave
   * triggered by an earlier selection could resolve and overwrite prices
   * AFTER a faster, more current wave already applied the right ones.
   * @param {string} currentFarbe
   */
  async #repriceOtherCards(currentFarbe) {
    this.#repriceAbortController.abort();
    this.#repriceAbortController = new AbortController();
    const { signal } = this.#repriceAbortController;

    const kissen = this.#readConfiguratorKissenSelection();
    const decke = this.#readConfiguratorDecke();

    const otherCards = this.#cards.filter((card) => card.farbe !== currentFarbe);

    await Promise.all(
      otherCards.map(async (card) => {
        const cardEl = this.querySelector(`.laken-recommendation-card[data-farbe="${CSS.escape(card.farbe)}"]`);
        const optionValueId = cardEl?.getAttribute('data-option-value-id');
        if (!optionValueId) return;

        const fetchedVariants = await this.#fetchVariantsForFarbe(optionValueId, signal);
        if (signal.aborted || !fetchedVariants) return;

        const { priceCents, compareAtCents } = this.#resolveConfiguratorPriceCents(
          fetchedVariants,
          kissen,
          decke,
          card.farbe
        );
        this.#renderCardPrice(card, priceCents, compareAtCents > priceCents ? compareAtCents : priceCents);
      })
    );
  }

  /**
   * Moves the `<slideshow-slide>` ancestor of the card matching `farbe` to
   * the first position among its siblings, so the shopper always sees "the
   * Laken that matches what I'm currently building" first. No-ops silently
   * if no card matches (e.g. the configurator switched to a Bettwäsche color
   * with no Laken recommendation).
   *
   * The shared `<slideshow-component>` (see `assets/slideshow.js`) tracks its
   * own "current slide" as a positional index that doesn't auto-follow DOM
   * reorders, so after the `prepend()` we call its public `select({ id })`
   * API (matched via each slide's stable `slide-id` attribute, set from
   * `farbe` in `sections/laken-recommendation.liquid`) to re-sync that index.
   * This fixes the deeper risk this comment used to under-describe: without
   * it, the carousel's arrow/drag navigation (which does positional math
   * against its internal index, not DOM order) could target the wrong
   * physical slide after a reorder.
   *
   * `select()` immediately marks the newly-selected slide `aria-hidden="false"`
   * and instantly scrolls to it (`{ animate: false }`); that scroll's
   * completion in turn re-runs the carousel's IntersectionObserver-driven
   * visibility sync, which stamps `aria-hidden` on every slide (not just the
   * target) based on what's actually visible. So the previously-known
   * "aria-hidden goes stale on the other slides" limitation is now resolved
   * as a side effect of re-syncing position, not left as a residual gap.
   *
   * The `select()` call itself must be deferred via `queueMicrotask`. Found by
   * tracing the actual source (not assumed): the carousel (`Slideshow`, in
   * `assets/slideshow.js`) extends this theme's `Component` base class
   * (`assets/component.js`), which keeps `this.refs.slides` in sync via a
   * `MutationObserver` — and per the DOM spec, `MutationObserver` callbacks are
   * ALWAYS invoked as a microtask, never synchronously with the mutation that
   * queued them. Call `select()` synchronously right after `prepend()`, in the
   * same tick, and its internal lookups run against the STALE, pre-reorder
   * `refs.slides` array. Deferring via `queueMicrotask` places our `select()`
   * call behind the observer's own already-queued callback in the microtask
   * FIFO, so `refs.slides` has been rebuilt to the new DOM order by the time
   * `select()` actually runs.
   *
   * That deferral introduces a SECOND, subtler hazard `select()` itself can't
   * recover from: it determines "is the requested slide already current"
   * (`assets/slideshow.js`, `select()`'s `currentSlide === requestedSlideElement`
   * check) by reading `this.slides[this.current]` — `this.slides` reflects the
   * NEW post-reorder order (by the time our deferred call runs), but
   * `this.current` (the plain index number) hasn't been updated yet, since
   * only `select()` itself ever updates it. So `this.slides[this.current]`
   * now accidentally points at the slide we JUST moved to the front — the very
   * slide we're trying to select — and `select()` concludes "nothing to do"
   * and returns immediately, before ever calling `this.#scroll.to()` or
   * updating `current` to the real new index. Net effect: the DOM reorder
   * succeeds (the right card ends up first) but the VISIBLE carousel viewport
   * never scrolls there, so the shopper sees some other, stale slide as
   * "current."
   *
   * Fixed by resyncing `carousel.current` to the previously-current slide's
   * NEW index (its element identity is unaffected by the reorder — only
   * `indexOf` changes) before calling `select()`. That restores the
   * invariant `select()` assumes — `this.slides[this.current]` again points
   * at whatever was actually on screen — so its "already current" check
   * correctly evaluates false for the just-moved target slide, and it
   * proceeds through its normal scroll/aria/index-update path.
   * @param {string} farbe
   */
  #reorderCards(farbe) {
    const matchingCard = this.querySelector(`.laken-recommendation-card[data-farbe="${CSS.escape(farbe)}"]`);
    const matchingSlide = matchingCard?.closest('slideshow-slide');
    if (!matchingSlide) return;

    const carousel = /** @type {import('slideshow').Slideshow | null} */ (this.querySelector('slideshow-component'));
    const previouslyCurrentSlide = carousel?.slides?.[carousel.current];

    matchingSlide.parentElement?.prepend(matchingSlide);

    // Defer past the microtask checkpoint so the carousel's own MutationObserver
    // (which rebuilds its `refs.slides` array to reflect the new DOM order) has
    // already run before we resync `current`/call `select()` — see the doc
    // comment above for why this is required, not optional.
    queueMicrotask(() => {
      if (carousel && previouslyCurrentSlide) {
        const resyncedIndex = carousel.slides?.indexOf(previouslyCurrentSlide);
        if (resyncedIndex !== undefined && resyncedIndex !== -1) carousel.current = resyncedIndex;
      }
      carousel?.select?.({ id: farbe }, undefined, { animate: false });
    });
  }

  /**
   * Tracks the most recent `handleCtaClick` invocation so a slower, earlier
   * click's async resolution can't clobber a faster, later click's popup
   * state — see the guard in `handleCtaClick` below.
   * @type {number}
   */
  #ctaClickToken = 0;

  /**
   * Opens the bundle-confirm popup for the card that was clicked, seeded with
   * a one-time read of the separate, already-shipped
   * `<configurator-variant-picker>`'s CURRENT selection. This is a read-at-
   * open-time snapshot only — the popup never writes back to the
   * configurator, matching this feature's one-way-read design.
   *
   * Bound via the declarative `on:click="laken-recommendation/handleCtaClick"`
   * attribute (see `sections/laken-recommendation.liquid`); per
   * `assets/component.js`'s dispatch logic, the resolved instance's method is
   * invoked as `callback.call(instance, proxiedEvent)`, so `event` here is
   * always the (possibly target-proxied) triggering event, never a
   * synthetic/positional argument.
   * @param {Event} event
   */
  async handleCtaClick(event) {
    const target = /** @type {Element | null} */ (event.target);
    const cardEl = target?.closest('.laken-recommendation-card');
    const farbe = cardEl?.getAttribute('data-farbe');
    if (!farbe) return;

    const card = this.#cards.find((c) => c.farbe === farbe);
    if (!card) return;

    const popup = /** @type {(HTMLElement & {
      open: (card: LakenCard, configuratorVariants: ConfiguratorVariant[], farbe: string, kissen: { kissenGroesse: string }[], decke: { deckeGroesse: string } | null, lieferzeitOrder: string) => void,
      openWithError: () => void,
    }) | null} */ (this.querySelector('laken-recommendation-popup'));
    if (!popup || typeof popup.open !== 'function') return;

    // Each click invalidates any still-in-flight fetch from a PREVIOUS click
    // on a different card — without this, clicking card A (slow fetch) then
    // quickly clicking card B (fast fetch) could let A's fetch resolve AFTER
    // B's and silently overwrite the already-open popup with A's data while
    // the shopper is looking at B's.
    const token = ++this.#ctaClickToken;

    let configuratorVariants = this.#readConfiguratorVariants();

    // The live configurator only ever embeds its OWN currently-selected
    // Farbe's variants (see blocks/configurator-variant-picker.liquid /
    // assets/configurator-variant-picker.js — Shopify hard-caps
    // product.variants at 250 in Liquid, so the full catalog can never be
    // embedded upfront). Clicking a card for a color other than whatever the
    // configurator currently shows means `configuratorVariants` has no
    // entries for `farbe` at all, so fetch that color's scoped set directly,
    // the same way the configurator refreshes its own on a Farbe change.
    if (!configuratorVariants.some((v) => v.farbe === farbe)) {
      const optionValueId = cardEl?.getAttribute('data-option-value-id');
      const fetched = await this.#fetchVariantsForFarbe(optionValueId);
      if (token !== this.#ctaClickToken) return; // superseded by a later click
      configuratorVariants = fetched ?? [];
    }

    // Still nothing for this Farbe after attempting a fetch (fetch failed, no
    // configurator-variant-picker element present at all, or the response
    // genuinely had no matching data) — open with a visible error rather than
    // silently rendering empty Kissen/Decke dropdowns the shopper could submit.
    if (!configuratorVariants.some((v) => v.farbe === farbe)) {
      popup.openWithError();
      return;
    }

    const rawKissen = this.#readConfiguratorKissenSelection();
    const rawDecke = this.#readConfiguratorDecke();
    const { kissen, decke } = this.#reconcileKissenForFarbe(rawKissen, rawDecke, farbe, configuratorVariants);

    const configuratorEl = document.querySelector('configurator-variant-picker');
    const lieferzeitOrder = configuratorEl?.dataset.lieferzeitOrder ?? '';

    popup.open(card, configuratorVariants, farbe, kissen, decke, lieferzeitOrder);
  }

  /**
   * Fetches the variant set scoped to a single Farbe via the Section
   * Rendering API (?section_id=...&option_values=...), mirroring
   * assets/configurator-variant-picker.js's own #fetchVariantsForFarbe.
   * Reads the response's own `<configurator-variant-picker>` variant data —
   * that block is the source of truth for {farbe, kissenGroesse,
   * deckeGroesse, lieferzeit, priceCents, available} regardless of which
   * component triggered the fetch.
   *
   * The section_id used is read from the LIVE configurator element's own
   * `data-section-id` (the `product-information` section hosting it, set in
   * blocks/configurator-variant-picker.liquid), not this element's own
   * section — `<laken-recommendation>` has no configurator-variant-picker
   * block of its own to render. This works even when this section's
   * configured product (`data-product-url`, from `section.settings.product`)
   * differs from the current page's product, because `product-information`
   * is templates/product.json's static "main" section (templates/product.json
   * key: "main") — Shopify derives its runtime section.id as
   * `template--<template_id>__main`, an ID shared by every product rendered
   * through that same template, not tied to a specific product's page.
   *
   * Returns null (no fetch attempted) if no `<configurator-variant-picker>`
   * element exists on the page at all — that block is independently
   * removable per its schema, so a merchant could delete it from a product
   * page while keeping laken-recommendation. The caller surfaces this as a
   * user-facing error rather than silently opening an empty popup.
   * @param {string | null | undefined} optionValueId
   * @param {AbortSignal} [signal]
   * @returns {Promise<ConfiguratorVariant[] | null>} null on failure or abort (caller keeps its prior data).
   */
  async #fetchVariantsForFarbe(optionValueId, signal) {
    const productUrl = this.dataset.productUrl;
    const sectionId = document.querySelector('configurator-variant-picker')?.dataset.sectionId;
    if (!productUrl || !sectionId || !optionValueId) return null;

    const url = `${productUrl}?section_id=${sectionId}&option_values=${optionValueId}`;

    try {
      const response = await fetch(url, { signal });
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const variantDataScript = html.querySelector('configurator-variant-picker script[type="application/json"]');
      if (!variantDataScript?.textContent) return null;

      return JSON.parse(variantDataScript.textContent);
    } catch (error) {
      if (error?.name !== 'AbortError') console.error(error);
      return null;
    }
  }

  /**
   * Reconciles the configurator's CURRENTLY SELECTED Kissen/Decke state
   * against the CLICKED card's Farbe before it's used to seed the popup.
   *
   * `#readConfiguratorKissenSelection`/`#readConfiguratorDecke` read whatever
   * the configurator currently shows — which reflects the configurator's own
   * currently-selected color, NOT necessarily the color of the card the
   * shopper just clicked (clicking any visible card, regardless of which
   * color the configurator is currently showing, is a completely normal
   * interaction). Seeding the popup with a size that's valid for one color
   * but not the CLICKED card's color would make
   * `laken-recommendation-popup.js`'s `#resolveKissenVariant` silently return
   * `undefined` for that instance, silently dropping that Kissen line from
   * the cart submission with no error and an under-priced popup total — see
   * the review that flagged this bug for the full trace.
   *
   * Keeps the same NUMBER of Kissen rows (never drops/adds instances), only
   * corrects invalid per-instance size VALUES: any instance whose
   * `kissenGroesse` has no matching variant for `farbe` falls back to the
   * first `kissenGroesse` found among variants for `farbe` (any bare size for
   * that color — "first found" is an intentionally simple, non-exhaustive
   * default, matching this codebase's existing sensible-default conventions
   * elsewhere, e.g. `handleAddKissen`'s `#availableKissenGroessen()[0] ?? ''`
   * in `assets/configurator-variant-picker.js`).
   *
   * Decke is corrected more conservatively: if the (possibly just-corrected)
   * Kissen-instance-0 size doesn't pair with the current Decke value for
   * `farbe`, Decke is reset to `null` ("Keine Decke") rather than
   * substituted, since "no blanket" is always a safe, always-valid fallback
   * — mirroring how the configurator's own `#renderDeckeGroup` treats "no
   * valid Decke options" as reason to clear the selection entirely.
   * @param {{ kissenGroesse: string }[]} kissen
   * @param {{ deckeGroesse: string } | null} decke
   * @param {string} farbe
   * @param {ConfiguratorVariant[]} configuratorVariants
   * @returns {{ kissen: { kissenGroesse: string }[], decke: { deckeGroesse: string } | null }}
   */
  #reconcileKissenForFarbe(kissen, decke, farbe, configuratorVariants) {
    const reconciledKissen = kissen.map((instance) => {
      const hasMatch = configuratorVariants.some(
        (v) => v.farbe === farbe && v.kissenGroesse === instance.kissenGroesse
      );
      if (hasMatch) return instance;

      const fallback = configuratorVariants.find((v) => v.farbe === farbe)?.kissenGroesse;
      return fallback === undefined ? instance : { kissenGroesse: fallback };
    });

    const kissen1Groesse = reconciledKissen[0]?.kissenGroesse;
    const deckeIsValid =
      decke !== null &&
      configuratorVariants.some(
        (v) => v.farbe === farbe && v.kissenGroesse === kissen1Groesse && v.deckeGroesse === decke.deckeGroesse
      );

    return { kissen: reconciledKissen, decke: deckeIsValid ? decke : null };
  }

  /**
   * Reads the live configurator's embedded variant catalog JSON. The
   * configurator (`<configurator-variant-picker>`, see
   * `blocks/configurator-variant-picker.liquid` /
   * `assets/configurator-variant-picker.js`) is a sibling element elsewhere
   * on the product page, not an ancestor/descendant of
   * `<laken-recommendation>`, so it's reached the same way this theme's other
   * cross-component lookups are done (e.g. `assets/product-form.js` finding
   * `cart-items-component`): via `document.querySelector` on its tag name.
   * @returns {ConfiguratorVariant[]}
   */
  #readConfiguratorVariants() {
    const configuratorEl = document.querySelector('configurator-variant-picker');
    const variantDataEl = configuratorEl?.querySelector('script[ref="variantData"]');
    if (!variantDataEl) return [];

    try {
      return JSON.parse(variantDataEl.textContent ?? '[]');
    } catch {
      return [];
    }
  }

  /**
   * Reads which Kissen-Größe is CURRENTLY SELECTED for each Kissen instance
   * in the live configurator. This can only be read from the configurator's
   * rendered DOM, not its embedded JSON (the JSON is a static variant
   * catalog with no notion of "what's currently checked").
   *
   * Per `assets/configurator-variant-picker.js`'s `#buildKissenRow` /
   * `#buildPillOption`, each Kissen instance renders as one
   * `<fieldset class="variant-option variant-option--buttons">` inside the
   * `ref="kissenList"` container (rendered class
   * `configurator-variant-picker__kissen-list`), containing one
   * `<label><input type="radio" name="configurator-kissen-{index}-{productId}">`
   * pill per size option, with the currently-selected size's radio carrying
   * `checked`. Reading, in DOM order, one checked radio's `value` per
   * fieldset reconstructs the same `{ kissenGroesse }[]` shape the
   * configurator keeps internally as `#kissen`.
   *
   * If `kissenList` is found but contains zero fieldsets, or a fieldset has
   * no checked radio inside it, this logs a `console.warn` (rather than
   * failing silently) so a future configurator-markup regression is visible
   * in devtools instead of just quietly rendering an empty/wrong popup.
   * @returns {{ kissenGroesse: string }[]}
   */
  #readConfiguratorKissenSelection() {
    const configuratorEl = document.querySelector('configurator-variant-picker');
    const kissenList = configuratorEl?.querySelector('.configurator-variant-picker__kissen-list');
    if (!kissenList) return [];

    const fieldsets = kissenList.querySelectorAll('fieldset');
    if (fieldsets.length === 0) {
      console.warn(
        'laken-recommendation: found .configurator-variant-picker__kissen-list but no <fieldset> rows inside it — configurator-variant-picker.js markup may have changed.'
      );
      return [];
    }

    /** @type {{ kissenGroesse: string }[]} */
    const kissen = [];
    for (const fieldset of fieldsets) {
      const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      if (checkedInput instanceof HTMLInputElement) {
        kissen.push({ kissenGroesse: checkedInput.value });
      } else {
        console.warn(
          'laken-recommendation: found a Kissen fieldset with no checked radio input — configurator-variant-picker.js markup may have changed.'
        );
      }
    }
    return kissen;
  }

  /**
   * Reads which Decke-Größe is CURRENTLY SELECTED in the live configurator
   * (or `null` for "Keine Decke" / no Decke selected). Like
   * `#readConfiguratorKissenSelection`, this can only be read from the
   * configurator's rendered DOM, not its embedded JSON.
   *
   * Per `assets/configurator-variant-picker.js`'s `#renderDeckeGroup`, the
   * `ref="deckeGroup"` container (rendered class
   * `configurator-variant-picker__decke`) holds ONE
   * `<fieldset class="variant-option variant-option--buttons">` with one
   * `<label><input type="radio" name="configurator-decke-{productId}">` pill
   * per real Decke size PLUS a synthetic pill with `value=""` representing
   * "Keine Decke" — "Keine Decke" is a real checked radio with an empty
   * value, not "no radio checked". Whichever radio carries `checked` is read;
   * an empty value means no Decke is selected (`null`).
   *
   * Unlike Kissen, an EMPTY `deckeGroup` container (`innerHTML = ''`, no
   * fieldset at all) is a valid, expected state — `#renderDeckeGroup` itself
   * early-returns without rendering anything when the current Farbe + Kissen
   * 1 combination has zero Decke pairings (e.g. a bare-only Kissen size), so
   * that case is NOT warned about here. A `console.warn` only fires when the
   * container holds a fieldset but that fieldset has no checked radio inside
   * it at all — that indicates an actual structural mismatch, not the
   * legitimate "no Decke options for this Kissen size" case.
   * @returns {{ deckeGroesse: string } | null}
   */
  #readConfiguratorDecke() {
    const configuratorEl = document.querySelector('configurator-variant-picker');
    const deckeGroup = configuratorEl?.querySelector('.configurator-variant-picker__decke');
    if (!deckeGroup) return null;

    const fieldset = deckeGroup.querySelector('fieldset');
    if (!fieldset) {
      // No fieldset at all is the legitimate "this Kissen size has zero Decke
      // pairings" state (#renderDeckeGroup's own early-return) — not an error.
      return null;
    }

    const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
    if (!(checkedInput instanceof HTMLInputElement)) {
      console.warn(
        'laken-recommendation: found a Decke fieldset with no checked radio input — configurator-variant-picker.js markup may have changed.'
      );
      return null;
    }

    return checkedInput.value === '' ? null : { deckeGroesse: checkedInput.value };
  }
}

if (!customElements.get('laken-recommendation')) {
  customElements.define('laken-recommendation', LakenRecommendation);
}
